import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Invokes `handler` when a pointerdown happens outside any of the given refs.
 * Pass `enabled = false` to skip attaching listeners (e.g. when the menu is closed).
 *
 * Uses `mousedown` rather than `click` so dropdowns feel instant and don't
 * swallow the subsequent click on a neighbouring target.
 */
export function useClickOutside(
  refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  handler: (event: MouseEvent) => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const refList = Array.isArray(refs) ? refs : [refs];

    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }
      for (const ref of refList) {
        if (ref.current?.contains(target)) {
          return;
        }
      }
      handler(event);
    }

    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [refs, handler, enabled]);
}
